import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface CashbackItem {
  id: number;
  level: string;
  title: string;
  description: string;
  type: string;
  image: string | File;
  isActive: boolean | number;
  checkingAt: string;
  loyaltyLevelId?: number;
  content?: string;
  claimExpirationHours?: number;
  bonusTemplateId?: number;
  eventSource?: string | null;
  wagerMultiplier?: string | null;
  configs?: {
    cashbackId?: number;
    currency: string;
    min: string;
    max: string;
    percentage: string;
    payoutCurrency?: string | null;
    bonusTemplateId?: number;
  }[];
}
export interface CashbackBunchPayload {
  ids: number[];
  isActive: number;
}

interface CashbacksState {
  list: CashbackItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: CashbackItem;
  isItemFetching: boolean;
  processingItemId?: CashbackItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  level: string;
  types: string[];
  isActive?: number;
  order: {
    title?: string;
    type?: string;
    id?: string;
    isActive?: string;
    level?: string;
  };
}

export const getCashbacksList = createAsyncThunk(
  "cashbacks/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.cashbacksList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateCashbacksBunch = createAsyncThunk(
  "cashbacks/update-bunch",
  async (data: CashbackBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.cashbackUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const createCashback = createAsyncThunk<
  number,
  CashbackItem,
  { rejectValue: ApiErrorPayload }
>("cashbacks/create", async (payload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.image instanceof File) {
    const safeName = sanitizeFileName(payload.image.name);

    data.append(
      "image",
      new File([payload.image], safeName, { type: payload.image.type }),
    );
  }
  data.append("title", payload?.title as string);
  data.append("level", payload?.level as string);
  data.append("description", payload.description ?? "");
  if (payload.checkingAt) {
    data.append("checkingAt", payload.checkingAt);
  }
  data.append("type", payload?.type as string);
  data.append("eventSource", payload.eventSource ?? "");
  data.append("wagerMultiplier", payload.wagerMultiplier == null ? "" : String(payload.wagerMultiplier));
  data.append("isActive", payload.isActive ? "1" : "0");
  data.append("content", payload.content ?? "");

  if (Number(payload.loyaltyLevelId) > 0) {
    data.append("loyaltyLevelId", Number(payload.loyaltyLevelId).toString());
  }
  if (payload.claimExpirationHours != null) {
    data.append(
      "claimExpirationHours",
      payload.claimExpirationHours.toString(),
    );
  }
  if (Number(payload.bonusTemplateId) > 0) {
    data.append("bonusTemplateId", Number(payload.bonusTemplateId).toString());
  }

  try {
    const result = await instance.post(`${apiRoutes.cashbackCreate}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return result.data.id;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});
export const updateCashback = createAsyncThunk(
  "cashbacks/update",
  async (payload: CashbackItem, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    data.append("title", payload?.title as string);
    data.append("level", payload?.level as string);
    data.append("description", payload.description ?? "");
    if (payload.checkingAt) {
      data.append("checkingAt", payload.checkingAt);
    }
    data.append("type", payload?.type as string);
  data.append("eventSource", payload.eventSource ?? "");
  data.append("wagerMultiplier", payload.wagerMultiplier == null ? "" : String(payload.wagerMultiplier));
    // Keep sending the current value until the backend no longer resets it.
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("content", payload.content ?? "");

    if (Number(payload.loyaltyLevelId) > 0) {
      data.append("loyaltyLevelId", Number(payload.loyaltyLevelId).toString());
    }
    if (payload.claimExpirationHours != null) {
      data.append(
        "claimExpirationHours",
        payload.claimExpirationHours.toString(),
      );
    }
    if (Number(payload.bonusTemplateId) > 0) {
      data.append(
        "bonusTemplateId",
        Number(payload.bonusTemplateId).toString(),
      );
    }

    try {
      await instance.put(`${apiRoutes.cashbackUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error: any) {
      return rejectWithValue(error?.data?.error ?? error?.data ?? {
        code: "InternalError", fieldErrors: [], globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const getCachbackItem = createAsyncThunk(
  "cashbacks/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.cashbackItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const deleteCachbackItem = createAsyncThunk(
  "cashbacks/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.cashbackDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: CashbacksState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
};

export const cashbacksSlice = createSlice({
  name: "cashbacks",
  initialState,
  reducers: {
    handlePaginate: (state: CashbacksState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleUpdateFields: (
      state: CashbacksState,
      action: PayloadAction<CashbackItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: CashbacksState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (
      state: CashbacksState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: CashbacksState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCashbacksList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createCashback.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createCashback.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createCashback.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateCashback.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateCashback.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateCashback.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteCachbackItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteCachbackItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteCachbackItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getCachbackItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
    });
    builder.addCase(updateCashbacksBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
} = cashbacksSlice.actions;

// updateCashbacksBunch is already exported above
